<script setup lang="ts">
/**
 * Bass-System erzeugen/entfernen – Prüfhilfe für die Akkorderkennung.
 *
 * Zeigt als klingende Noten im Bassschlüssel, welchen Wechselbass die Harmonika zu den erkannten
 * Akkorden spielen würde. Damit ist vor der Konvertierung hörbar und sichtbar, ob die Akkorde
 * stimmen. Der Part ist reine Anzeige: Analyse und Konvertierung klammern ihn aus.
 */
import {computed, ref} from 'vue';
import {Icon} from '@iconify/vue';
import {storeToRefs} from 'pinia';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';
import {useNotificationStore} from '@/stores/notifications';
import {useSettingsStore} from '@/stores/settings';
import {HANDWRITTEN_BASS_COLOR, INFERRED_CHORD_COLOR} from '@/composables/musicXmlDom';

const notificationStore = useNotificationStore();
const uploadStore = useMusicUploadStore();
const settingsStore = useSettingsStore();

const {currentFileId, isAnalyzing, isBuildingBassPreview, bassPreviewState, hasChordSymbols} =
    storeToRefs(uploadStore);
const {bassLayout} = storeToRefs(settingsStore);

const busy = computed(() => isBuildingBassPreview.value || isAnalyzing.value);
const exists = computed(() => bassPreviewState.value !== 'none');

const status = ref('');

const STATE_TEXT = {
  none: 'Noch kein Bass-System vorhanden.',
  generated: 'Vollständig erzeugt – ein erneutes Erzeugen aktualisiert alle Takte.',
  edited: 'Enthält eigene Takte (dunkelblau). Diese bleiben beim erneuten Erzeugen unverändert '
      + 'und bestimmen die Bass-Zeile der Griffschrift.',
} as const;

async function build() {
  if (!currentFileId.value) {
    notificationStore.notify('Bitte zunächst eine Datei hochladen.', 'warning', 2000);
    return;
  }
  status.value = 'Bass-System wird erzeugt…';
  const result = await uploadStore.applyBassPreview(false);
  status.value = result ? 'Bass-System erzeugt.' : '';
  if (result) notificationStore.notify('Bass-System erzeugt.', 'success', 2000);
}

async function remove() {
  status.value = 'Bass-System wird entfernt…';
  const result = await uploadStore.applyBassPreview(true);
  status.value = result ? 'Bass-System entfernt.' : '';
  if (result) notificationStore.notify('Bass-System entfernt.', 'success', 2000);
}
</script>

<template>
  <div class="max-w-full">
    <div v-if="status" class="mb-4 flex justify-end">
      <div
          class="px-3 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100 animate-fade-in"
      >
        {{ status }}
      </div>
    </div>

    <div class="space-y-4">
      <p class="text-sm text-gray-600">
        Ergänzt ein zweites System im Bassschlüssel mit dem Wechselbass, den die Harmonika zu den
        Akkordsymbolen spielen würde – zum Prüfen der Akkorde vor dem Konvertieren.
      </p>

      <div
          v-if="!hasChordSymbols"
          class="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
      >
        <Icon icon="fa6-solid:triangle-exclamation" class="mt-0.5 shrink-0"/>
        <span>Das Stück hat noch keine Akkordsymbole. Zuerst im Tab „Akkorderkennung" erkennen lassen
          – sonst bleibt das Bass-System stumm.</span>
      </div>

      <div class="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
        <p class="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">Zustand</p>
        <p class="text-sm text-gray-700">{{ STATE_TEXT[bassPreviewState] }}</p>
        <p v-if="exists" class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
          <span class="inline-flex items-center gap-1.5">
            <span class="h-2.5 w-2.5 rounded-full" :style="{backgroundColor: INFERRED_CHORD_COLOR}"/>
            maschinell erzeugt
          </span>
          <span class="inline-flex items-center gap-1.5">
            <span class="h-2.5 w-2.5 rounded-full" :style="{backgroundColor: HANDWRITTEN_BASS_COLOR}"/>
            von Hand geschrieben
          </span>
        </p>
        <p class="mt-2 text-xs text-gray-500">
          Belegung: <span class="font-medium text-gray-700">{{ bassLayout.name }}</span>
          – änderbar im Tab „Harmonika" unter „Bass-Belegung".
        </p>
      </div>

      <div class="flex flex-wrap gap-2">
        <button
            id="applyBassPreview"
            type="button"
            class="flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="busy"
            @click="build"
        >
          <Icon
              :icon="isBuildingBassPreview ? 'fa6-solid:spinner' : 'game-icons:f-clef'"
              :class="isBuildingBassPreview ? 'animate-spin' : ''"
          />
          {{ exists ? 'Neu erzeugen' : 'Bass-System erzeugen' }}
        </button>

        <button
            v-if="exists"
            id="removeBassPreview"
            type="button"
            class="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="busy"
            @click="remove"
        >
          <Icon icon="fa6-solid:trash-can"/>
          Entfernen
        </button>
      </div>

      <p class="text-xs text-gray-500">
        Das Bass-System wird beim Konvertieren nicht übernommen – die Griffschrift-Bass-Zeile
        entsteht aus den Akkorden. Wirkung auf die Ausgabe hat daher nur, was hier an den
        <strong>Akkorden</strong> korrigiert wird.
      </p>
    </div>
  </div>
</template>
